/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { sortBy, uniq, uniqBy } from "lodash";
import { OLAPTransformerResult, useParameter, useDictionary } from "../../../shared";
import { dictionaryService } from "../../../dataFlow";
import { TableProps } from "../../../components/Table";
import { ColDef } from "ag-grid-community";
import { SLIDE_NUMBER } from "../constants";
import { getDateParts } from "../utils/dateHelpers";
import { AgGridHeaderComponent } from "../controls/AgGridColumnHeader";

const COLUMNS_AXIS = "commodities.default";
const UNITS_AXIS = "units.default";
const ROWS_AXIS = "calendar.default";
const [YEAR_FREQUENCY, QUARTER_FREQUENCY, MONTH_FREQUENCY, WEEK_FREQUENCY] = [1, 3, 4, 7];

export function useAgGridTransformer(olapData: OLAPTransformerResult | null): TableProps | undefined {
  const [state, setState] = useState<TableProps | undefined>();
  const [commodities] = useParameter<number[]>("@commodities");
  const [commoditiesColumnsDisplay] = useDictionary(dictionaryService, "slides-commodities-display", undefined, {
    slide: SLIDE_NUMBER,
    business_unit: 1,
  });

  const calendarParameters = useMemo(() => {
    if (olapData != null) {
      return olapData.rows.map((row) => row.attributes[`${ROWS_AXIS}.id`]);
    }
  }, [olapData]);

  const calendarHierarchy = useMemo(() => {
    if (calendarParameters != null && olapData != null) {
      const calendarParts = calendarParameters.map((item) => getDateParts(item));
      const sortedCalendarParts = sortBy(calendarParts, ["year", "month", "frequency"]);

      return uniq(sortedCalendarParts.map(({ frequency, year, month, day }) => `${frequency}${year}${month}${day}`));
    }
  }, [calendarParameters, olapData]);

  const units = useMemo(
    () =>
      uniqBy(olapData?.rows, (row) => row.selection[`${UNITS_AXIS}`]).map((item) => ({
        id: item.attributes[`${UNITS_AXIS}.id`],
        name: item.attributes[`${UNITS_AXIS}.name`][9],
      })),
    [olapData],
  );

  const categories = useMemo(() => {
    const result: ColDef[] = [];

    commodities?.forEach((commodityId) => {
      const column = olapData?.columns.find((col) => {
        return col.selection[COLUMNS_AXIS] === commodityId;
      });
      const colHeader = column?.headers?.find((header) => header.id === `${COLUMNS_AXIS}.name`);
      let localeName = colHeader?.name as any;
      if (localeName === undefined) {
        const displayItem = commoditiesColumnsDisplay?.find(
          (item) => item.getValue<number>("olap_commodity") === commodityId,
        );
        localeName = displayItem?.getValue<any>("name");
      }
      if (typeof localeName === "object") {
        localeName = localeName[9];
      }
      const name = localeName || "";

      units.forEach((unit) => {
        const headerName = [name, unit.name].filter(Boolean).join(", ");

        result.push({
          headerName,
          field: `${commodityId}:${unit.id}`,
          resizable: true,
          minWidth: 100,
          headerComponentFramework: AgGridHeaderComponent,
        });
      });
    });

    return result;
  }, [olapData, commodities, units]);

  useEffect(() => {
    if (olapData) {
      const rows =
        calendarHierarchy?.flatMap((calendarId) => {
          const row = olapData?.rows.find((row) => Number(row.selection[ROWS_AXIS]) === Number(calendarId));
          const { frequency } = getDateParts(calendarId);

          let rowName = row?.attributes[`${ROWS_AXIS}.name`];
          const nFrequency = Number(frequency);
          if (nFrequency === WEEK_FREQUENCY) {
            rowName = `W${rowName}`;
          }

          const recordsRow = {
            id: calendarId,
            Period: rowName,
          };

          units.forEach((unit) => {
            const dataRow = olapData?.rows.find(
              (row) =>
                Number(row.selection[ROWS_AXIS]) === Number(calendarId) &&
                Number(row.selection[UNITS_AXIS]) === Number(unit.id),
            );
            commodities?.forEach((commodityId) => {
              const cell = dataRow?.points.find(
                (point) => point?.column?.selection && point.column.selection[COLUMNS_AXIS] === commodityId,
              );
              const isEmpty = cell?.value === undefined || cell?.value === null;
              recordsRow[`${commodityId}:${unit.id}`] = isEmpty ? "" : cell!.value.toFixed(2);
            });
          });

          return recordsRow;
        }) || [];

      const periodCol: ColDef = {
        headerName: "",
        field: "Period",
        resizable: true,
        minWidth: 170,
        pinned: "left",
      };

      const result: TableProps = {
        columns: [periodCol].concat(categories),
        rows,
        getRowClass: ({ data }) => {
          const { frequency } = getDateParts(data.id);

          const nFrequency = Number(frequency);
          const yearClassName = nFrequency === YEAR_FREQUENCY && "year-row";
          const quarterClassName = nFrequency === QUARTER_FREQUENCY && "quarter-row";
          const monthClassName = nFrequency === MONTH_FREQUENCY && "month-row";
          const weekClassName = nFrequency === WEEK_FREQUENCY && "week-row";

          return [yearClassName, quarterClassName, monthClassName, weekClassName].filter(Boolean).join(" ");
        },
      };

      setState(result);
    } else {
      setState(undefined);
    }
  }, [olapData, calendarParameters]);

  return state;
}
